import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Sparkles, Clock, AlertCircle, CheckCircle2, SkipForward, Lock, GraduationCap, FileText } from 'lucide-react';

const TimelineWorkflowHierarchical = () => {
  const [expandedCollege, setExpandedCollege] = useState(true);
  const [expandedWorkflows, setExpandedWorkflows] = useState({
    'essay-1': true,
    'essay-2': false
  });

  const toggleWorkflow = (id) => {
    setExpandedWorkflows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div className="min-h-screen bg-stone-50 p-8 font-['Reddit_Sans',system-ui,sans-serif]">
      <div className="max-w-6xl mx-auto space-y-8">
        
        {/* LEVEL 1: COLLEGE APPLICATION - Maximum emphasis */}
        <div className="bg-white rounded-2xl border border-slate-900 overflow-hidden shadow-lg">
          
          {/* College Header - Heaviest weight */}
          <div className="bg-slate-900 border-b-4 border-teal-500">
            <button
              onClick={() => setExpandedCollege(!expandedCollege)}
              className="w-full flex items-center gap-5 p-6 hover:bg-slate-800 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-teal-500 border border-white flex items-center justify-center flex-shrink-0">
                <GraduationCap className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 text-left">
                <h2 className="text-lg font-bold text-white mb-1">
                  Duke University Application
                </h2>
                <p className="text-sm text-slate-300">
                  10 workflows · 3 complete · 2 in progress · 5 not started
                </p>
              </div>
              <div className="text-right mr-4">
                <div className="text-2xl font-bold text-white mb-1">32%</div>
                <div className="text-xs text-slate-400">Overall progress</div>
              </div>
              {expandedCollege ? (
                <ChevronUp className="w-6 h-6 text-white flex-shrink-0" />
              ) : (
                <ChevronDown className="w-6 h-6 text-white flex-shrink-0" />
              )}
            </button>
          </div>

          {/* College workflows container */}
          {expandedCollege && (
            <div className="p-6 space-y-4 bg-stone-50/50">
              
              {/* LEVEL 2: ESSAY WORKFLOW - Medium emphasis */}
              <div className="bg-white rounded-xl border border-slate-500 overflow-hidden shadow">
                
                {/* Essay Workflow Header - Medium weight */}
                <button
                  onClick={() => toggleWorkflow('essay-1')}
                  className="w-full flex items-start gap-4 p-4 bg-slate-100 hover:bg-slate-200 transition-colors border-b-2 border-slate-300"
                >
                  <div className="w-8 h-8 rounded-lg bg-teal-100 border border-teal-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FileText className="w-4 h-4 text-teal-700" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-sm font-bold text-slate-900 mb-1">
                      Essay: We seek a diverse student body that embodies the wide ran...
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-slate-600">
                      <span>6 steps · Step 1 of 6 in progress</span>
                      <span className="inline-flex items-center gap-1 text-slate-500">
                        <Clock className="w-3 h-3" />
                        ~5.5 hr total
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-coral-100 border border-coral-400 text-xs font-medium text-coral-800">
                      <AlertCircle className="w-3 h-3" />
                      2 overdue
                    </span>
                    {expandedWorkflows['essay-1'] ? (
                      <ChevronUp className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    )}
                  </div>
                </button>

                {/* LEVEL 3: SEQUENTIAL STEPS */}
                {expandedWorkflows['essay-1'] && (
                  <div className="p-5 bg-white">
                    
                    {/* Flow container with left progress line */}
                    <div className="relative">
                      {/* Vertical progress line */}
                      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-200"></div>
                      
                      <div className="space-y-3">
                        
                        {/* STEP 1 - CURRENT ACTIVE */}
                        <div className="relative pl-14">
                          <div className="absolute left-0 top-3 w-10 h-10 rounded-full bg-teal-500 border border-white shadow-md flex items-center justify-center z-10">
                            <span className="text-xs font-bold text-white">1</span>
                          </div>
                          
                          <div className="bg-teal-50 rounded-lg border border-teal-500 shadow-sm">
                            <div className="p-4">
                              <div className="flex items-start gap-3 mb-3">
                                <input 
                                  type="checkbox" 
                                  className="mt-0.5 w-4 h-4 rounded border border-teal-600"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold-200 border border-gold-500 text-xs font-bold text-gold-900">
                                      <Clock className="w-3 h-3" />
                                      CURRENT
                                    </span>
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-coral-100 border border-coral-400 text-xs font-medium text-coral-800">
                                      <AlertCircle className="w-3 h-3" />
                                      Due Oct 25
                                    </span>
                                  </div>
                                  <h4 className="text-sm font-semibold text-slate-900 mb-1.5">
                                    Brainstorm concepts for essay
                                  </h4>
                                  <p className="text-xs text-slate-600 mb-3">
                                    Start with brainstorming to explore all angles before committing to a direction
                                  </p>
                                  
                                  {/* Time Estimate with Dropdown */}
                                  <details className="mb-3 group">
                                    <summary className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white border border-teal-400 text-xs font-medium text-slate-700 cursor-pointer hover:bg-teal-50 transition-colors list-none">
                                      <Clock className="w-3.5 h-3.5 text-teal-600" />
                                      <span className="font-semibold text-slate-900">45 min</span>
                                      <span className="text-slate-500">· Standard Session</span>
                                      <ChevronDown className="w-3 h-3 text-slate-500 group-open:rotate-180 transition-transform" />
                                    </summary>
                                    <div className="mt-2 p-3 bg-white rounded-lg border border-slate-300 shadow-sm space-y-2">
                                      <button className="w-full text-left p-3 rounded-lg bg-white border border-teal-500 hover:bg-teal-50 transition-colors">
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                          <span className="text-xs font-bold text-slate-900">Standard Session</span>
                                          <span className="text-xs font-bold text-teal-700">45 min</span>
                                        </div>
                                        <p className="text-xs text-slate-700 mb-2">
                                          8-10 concepts with guided exploration
                                        </p>
                                        <p className="text-xs text-slate-500 italic">
                                          Best when you have time to think deeply
                                        </p>
                                      </button>
                                      <button className="w-full text-left p-3 rounded-lg bg-white border border-slate-300 hover:bg-stone-50 transition-colors">
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                          <span className="text-xs font-bold text-slate-900">Express Session</span>
                                          <span className="text-xs font-bold text-coral-700">20 min</span>
                                        </div>
                                        <p className="text-xs text-slate-700 mb-2">
                                          3-4 core concepts, rapid ideation
                                        </p>
                                        <p className="text-xs text-slate-500 italic">
                                          Best when you need to keep momentum
                                        </p>
                                      </button>
                                      <p className="text-xs text-slate-500 pt-1 text-center">
                                        You can always return and expand later
                                      </p>
                                    </div>
                                  </details>
                                  
                                  <button className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 transition-all border border-slate-900 shadow-sm">
                                    <Sparkles className="w-3.5 h-3.5" />
                                    Essay Ideation
                                    <ChevronDown className="w-3.5 h-3.5 rotate-[-90deg]" />
                                  </button>
                                </div>
                                <button className="px-2 py-1 rounded border border-slate-300 text-xs text-slate-500 hover:bg-white transition-colors">
                                  Skip
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* STEP 2 - UPCOMING */}
                        <div className="relative pl-14">
                          <div className="absolute left-0 top-2 w-10 h-10 rounded-full bg-white border border-slate-300 flex items-center justify-center z-10">
                            <span className="text-xs font-bold text-slate-600">2</span>
                          </div>
                          
                          <div className="bg-white rounded-lg border border-slate-300">
                            <div className="p-3 flex items-start gap-2.5">
                              <input 
                                type="checkbox" 
                                className="mt-0.5 w-3.5 h-3.5 rounded border border-slate-400"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h4 className="text-xs font-medium text-slate-700">
                                    Write first draft of essay
                                  </h4>
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 text-xs text-slate-600 flex-shrink-0">
                                    <Clock className="w-2.5 h-2.5" />
                                    2 hr
                                  </span>
                                </div>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-coral-50 border border-coral-200 text-xs text-coral-700">
                                  <AlertCircle className="w-2.5 h-2.5" />
                                  Due Oct 25
                                </span>
                              </div>
                              <button className="px-2 py-0.5 rounded border border-stone-300 text-xs text-slate-500 hover:bg-stone-50">
                                Skip
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* STEP 3 - UPCOMING */}
                        <div className="relative pl-14">
                          <div className="absolute left-0 top-2 w-10 h-10 rounded-full bg-white border border-slate-300 flex items-center justify-center z-10">
                            <span className="text-xs font-bold text-slate-600">3</span>
                          </div>
                          
                          <div className="bg-white rounded-lg border border-slate-300">
                            <div className="p-3 flex items-start gap-2.5">
                              <input 
                                type="checkbox" 
                                className="mt-0.5 w-3.5 h-3.5 rounded border border-slate-400"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h4 className="text-xs font-medium text-slate-700">
                                    Get AI Analysis and Feedback
                                  </h4>
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 text-xs text-slate-600 flex-shrink-0">
                                    <Clock className="w-2.5 h-2.5" />
                                    15 min
                                  </span>
                                </div>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-coral-50 border border-coral-200 text-xs text-coral-700">
                                  <AlertCircle className="w-2.5 h-2.5" />
                                  Due Oct 25
                                </span>
                              </div>
                              <button className="px-2 py-0.5 rounded border border-stone-300 text-xs text-slate-500 hover:bg-stone-50">
                                Skip
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* STEP 4 - UPCOMING */}
                        <div className="relative pl-14">
                          <div className="absolute left-0 top-2 w-10 h-10 rounded-full bg-white border border-slate-300 flex items-center justify-center z-10">
                            <span className="text-xs font-bold text-slate-600">4</span>
                          </div>
                          
                          <div className="bg-white rounded-lg border border-slate-300">
                            <div className="p-3 flex items-start gap-2.5">
                              <input 
                                type="checkbox" 
                                className="mt-0.5 w-3.5 h-3.5 rounded border border-slate-400"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h4 className="text-xs font-medium text-slate-700">
                                    Share essay for Review
                                  </h4>
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 text-xs text-slate-600 flex-shrink-0">
                                    <Clock className="w-2.5 h-2.5" />
                                    10 min
                                  </span>
                                </div>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-coral-50 border border-coral-200 text-xs text-coral-700">
                                  <AlertCircle className="w-2.5 h-2.5" />
                                  Due Oct 25
                                </span>
                              </div>
                              <button className="px-2 py-0.5 rounded border border-stone-300 text-xs text-slate-500 hover:bg-stone-50">
                                Skip
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* STEP 5 */}
                        <div className="relative pl-14">
                          <div className="absolute left-0 top-2 w-10 h-10 rounded-full bg-white border border-slate-300 flex items-center justify-center z-10">
                            <span className="text-xs font-bold text-slate-600">5</span>
                          </div>
                          
                          <div className="bg-white rounded-lg border border-slate-300">
                            <div className="p-3 flex items-start gap-2.5">
                              <input 
                                type="checkbox" 
                                className="mt-0.5 w-3.5 h-3.5 rounded border border-slate-400"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                  <h4 className="text-xs font-medium text-slate-700">
                                    Revise and Polish essay
                                  </h4>
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 text-xs text-slate-600 flex-shrink-0">
                                    <Clock className="w-2.5 h-2.5" />
                                    1.5 hr
                                  </span>
                                </div>
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-coral-50 border border-coral-200 text-xs text-coral-700">
                                  <AlertCircle className="w-2.5 h-2.5" />
                                  Due Oct 25
                                </span>
                              </div>
                              <button className="px-2 py-0.5 rounded border border-stone-300 text-xs text-slate-500 hover:bg-stone-50">
                                Skip
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* STEP 6 - LOCKED */}
                        <div className="relative pl-14">
                          <div className="absolute left-0 top-2 w-10 h-10 rounded-full bg-stone-100 border border-stone-300 flex items-center justify-center z-10">
                            <span className="text-xs font-bold text-stone-400">6</span>
                          </div>
                          
                          <div className="bg-white rounded-lg border border-stone-200 opacity-40">
                            <div className="p-3 flex items-start gap-2.5">
                              <input 
                                type="checkbox"
                                disabled
                                className="mt-0.5 w-3.5 h-3.5 rounded border border-stone-300"
                              />
                              <div className="flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <h4 className="text-xs font-medium text-slate-500 flex items-center gap-1.5">
                                    Finalize essay
                                    <Lock className="w-3 h-3" />
                                  </h4>
                                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-stone-100 text-xs text-stone-500 flex-shrink-0">
                                    <Clock className="w-2.5 h-2.5" />
                                    30 min
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Another essay workflow - collapsed */}
              <div className="bg-white rounded-xl border border-slate-500 overflow-hidden shadow">
                <button
                  onClick={() => toggleWorkflow('essay-2')}
                  className="w-full flex items-start gap-4 p-4 bg-slate-100 hover:bg-slate-200 transition-colors"
                >
                  <div className="w-8 h-8 rounded-lg bg-slate-200 border border-slate-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <h3 className="text-sm font-bold text-slate-900 mb-1">
                      Essay: Duke's Commitment To Diversity And Inclus
                    </h3>
                    <p className="text-xs text-slate-600">
                      6 steps · Not started
                    </p>
                  </div>
                  <ChevronDown className="w-5 h-5 text-slate-600" />
                </button>
              </div>

              {/* Placeholder for more workflows */}
              <div className="text-center py-4 text-sm text-slate-500">
                + 8 more workflows
              </div>

            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default TimelineWorkflowHierarchical;